import { useEffect, useRef } from "react";
import { Box, Checkbox, GlobalStyles } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";
import {
	LIST_ROW_CLASS,
	NUMBER_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import type { StraightenerParameterRow } from "../models/StraightenerParamLimit";

interface Props {
	rows: StraightenerParameterRow[];
	selectedCode: string | null;
	onSelect: (row: StraightenerParameterRow) => void;
	/** Flips back to true when a held selection is given up, so the grid points at the edited row again. */
	resync: boolean;
}

// The last column sits against the vertical scrollbar: a wider right padding keeps the
// numbers off it.
const LAST_NUMBER_CELL_CLASS = "straightener-limits-last-number";

const flagColumn = (
	field: "sendToPlc" | "isRequired",
	headerName: string,
): ColDef<StraightenerParameterRow> => ({
	field,
	headerName,
	flex: 0.6,
	minWidth: 80,
	sortable: true,
	cellRenderer: (params: ICellRendererParams<StraightenerParameterRow>) =>
		params.data ? (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
				}}
			>
				<Checkbox
					checked={!!params.data[field]}
					size="small"
					disabled
					sx={{ p: 0 }}
				/>
			</Box>
		) : null,
});

const columnDefs: ColDef<StraightenerParameterRow>[] = [
	{
		field: "displayName",
		headerName: "Parameter",
		flex: 1.8,
		minWidth: 140,
		sortable: true,
		tooltipField: "displayName",
		cellClass: ONE_LINE_CELL_CLASS,
	},
	{
		field: "group",
		headerName: "Section",
		flex: 1.6,
		minWidth: 100,
		sortable: true,
		cellClass: ONE_LINE_CELL_CLASS,
	},
	flagColumn("sendToPlc", "To PLC"),
	flagColumn("isRequired", "Required"),
	{
		field: "min",
		headerName: "Min",
		width: 80,
		sortable: true,
		cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
	},
	{
		field: "max",
		headerName: "Max",
		width: 80,
		sortable: true,
		cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS, LAST_NUMBER_CELL_CLASS],
	},
];

const defaultColDef = listColDef<StraightenerParameterRow>(globalDefaultColDef);

export const StraightenerLimitsTable = ({
	rows,
	selectedCode,
	onSelect,
	resync,
}: Props) => {
	const gridRef = useRef<AgGridReact<StraightenerParameterRow>>(null);
	const onCellKeyDown =
		useArrowRowNavigation<StraightenerParameterRow>(onSelect);

	// The editor may keep the previous parameter while it holds unsaved edits, so the
	// highlighted row follows what the editor shows, not the last click or arrow.
	useEffect(() => {
		if (!resync) return;
		gridRef.current?.api?.forEachNode((node) =>
			node.setSelected(node.data?.code === selectedCode),
		);
	}, [selectedCode, rows, resync]);

	return (
		<>
			<OneLineCellStyles />
			<GlobalStyles
				styles={{
					[`.ag-cell.${LAST_NUMBER_CELL_CLASS}`]: {
						paddingRight: "16px !important",
					},
				}}
			/>
			<AgGridReact<StraightenerParameterRow>
				ref={gridRef}
				rowData={rows}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={(params) => params.data.code}
				onRowClicked={(event) => event.data && onSelect(event.data)}
				onCellKeyDown={onCellKeyDown}
				onFirstDataRendered={(event) =>
					event.api.forEachNode((node) =>
						node.setSelected(node.data?.code === selectedCode),
					)
				}
				rowSelection="single"
				suppressContextMenu
				alwaysShowVerticalScroll
				suppressColumnVirtualisation
				animateRows={false}
				rowClass={LIST_ROW_CLASS}
				rowHeight={LIST_ROW_HEIGHT}
				headerHeight={LIST_HEADER_HEIGHT}
				enableBrowserTooltips
				overlayNoRowsTemplate="No parameters match the search."
				getRowStyle={(params) =>
					params.data?.code === selectedCode
						? { backgroundColor: "#e3f2fd", color: "inherit" }
						: undefined
				}
			/>
		</>
	);
};
